import React, { useState, useEffect } from 'react';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CForm,
  CFormSelect,
  CFormLabel,
  CSpinner,
  CAlert,
  CBadge
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
  cilCog, 
  cilCopy,
  cilCheckCircle,
  cilReload
} from '@coreui/icons';
import { superAdminAPI } from '../services/api';

const SuperAdminSerialNoGeneration = () => {
  const [loading, setLoading] = useState({
    products: false,
    generation: false
  });
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('11');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [generatedData, setGeneratedData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);

  // Available versions
  const versions = [
    { value: '9', label: 'Version 9' },
    { value: '10', label: 'Version 10' },
    { value: '11', label: 'Version 11' }
  ];

  // Function to filter products based on selected version
  const filterProductsByVersion = (products, version) => {
    return products.filter(product => {
      const productName = product.vchproduct;
      if (version === '9') {
        return productName.includes(' 9') || 
               productName.includes('9.0') ||
               productName.includes('900') ||
               productName.includes('901') ||
               productName.includes('902') ||
               productName.includes('903') ||
               productName.includes('90T') ||
               productName.includes('9.5') ||
               productName.includes('090E');
      } else if (version === '10') {
        return productName.includes('10') || productName.includes('100');
      } else if (version === '11') {
        return productName.includes('11') || productName.includes('110');
      }
      return false;
    });
  };

  // Fetch products for dropdown
  const fetchProducts = async () => {
    setLoading(prev => ({ ...prev, products: true }));
    setError('');
    try {
      const response = await superAdminAPI.getProductsForSerial();
      if (response.data.success) {
        setAllProducts(response.data.products);
        // Initial filter for version 11 (default)
        const initialFiltered = filterProductsByVersion(response.data.products, '11');
        setFilteredProducts(initialFiltered);
        // Auto-select first product if available
        if (initialFiltered.length > 0) {
          setSelectedProduct(initialFiltered[0].vchproduct);
        }
      } else {
        setError(response.data.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch products');
    } finally {
      setLoading(prev => ({ ...prev, products: false }));
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle version change
  useEffect(() => {
    if (allProducts.length > 0) {
      const filtered = filterProductsByVersion(allProducts, selectedVersion);
      setFilteredProducts(filtered);
      // Auto-select first product of the selected version
      if (filtered.length > 0) {
        setSelectedProduct(filtered[0].vchproduct);
      } else {
        setSelectedProduct('');
      }
    }
  }, [selectedVersion, allProducts]);

  const handleGenerate = async () => {
    if (!selectedProduct) {
      setError('Please select a product');
      return;
    }

    setLoading(prev => ({ ...prev, generation: true }));
    setError('');
    setSuccess('');
    setGeneratedData(null);
    setCopied(false);

    try {
      console.log("Sending request for product:", selectedProduct);
      const response = await superAdminAPI.generateSerialNumber(selectedProduct);
      console.log("API Response:", response.data);
      
      if (response.data.success) {
        const apiData = response.data.data || response.data;
        setGeneratedData(apiData);
        setSuccess(response.data.message || `Successfully generated serial number for ${selectedProduct}`);
      } else {
        setError(response.data.error || response.data.message || 'Failed to generate serial number');
      }
    } catch (err) {
      console.error("API Error:", err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to generate serial number');
    } finally {
      setLoading(prev => ({ ...prev, generation: false }));
    }
  };

  const handleCopyToClipboard = () => {
    if (!generatedData || !generatedData.serial_number) return;
    
    navigator.clipboard.writeText(generatedData.serial_number)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch(() => setError('Failed to copy to clipboard'));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            <CIcon icon={cilCog} className="me-2 text-primary" />
            Serial Number Generation
            <CBadge color="primary" className="ms-2">
              <CIcon icon={cilCog} className="me-1" />
              Super Admin
            </CBadge>
          </h2>
          <p className="text-medium-emphasis">
            Generate serial numbers for software products
          </p>
        </div>
        <CButton 
          color="primary" 
          onClick={fetchProducts}
          disabled={loading.products}
          variant="outline"
        >
          <CIcon icon={cilReload} className="me-2" />
          Refresh Products
        </CButton>
      </div>

      {/* Error Alert */}
      {error && (
        <CAlert color="danger" className="mb-3" onClose={() => setError('')} dismissible>
          {error}
        </CAlert>
      )}

      {/* Success Alert */}
      {success && (
        <CAlert color="success" className="mb-3" onClose={() => setSuccess('')} dismissible>
          {success}
        </CAlert>
      )}

      <CRow>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <h5>Generate Serial Number</h5>
            </CCardHeader>
            <CCardBody>
              <CForm>
                {/* Version Selection Dropdown */}
                <div className="mb-4">
                  <CFormLabel htmlFor="versionSelect" className="fw-bold">
                    Select Version
                  </CFormLabel>
                  <CFormSelect
                    id="versionSelect"
                    value={selectedVersion}
                    onChange={(e) => setSelectedVersion(e.target.value)}
                    size="lg"
                  >
                    {versions.map((version) => (
                      <option key={version.value} value={version.value}>
                        {version.label}
                      </option>
                    ))}
                  </CFormSelect>
                </div>

                {/* Product Selection Dropdown */}
                <div className="mb-4">
                  <CFormLabel htmlFor="productSelect" className="fw-bold">
                    Select Product
                  </CFormLabel>
                  {loading.products ? (
                    <div className="d-flex align-items-center">
                      <CSpinner size="sm" className="me-2" />
                      <span>Loading products...</span>
                    </div>
                  ) : (
                    <CFormSelect
                      id="productSelect"
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                      disabled={filteredProducts.length === 0}
                      size="lg"
                    >
                      <option value="">Select a product...</option>
                      {filteredProducts.map((product, index) => (
                        <option key={index} value={product.vchproduct}>
                          {product.vchproduct}
                        </option>
                      ))}
                    </CFormSelect>
                  )}
                  {filteredProducts.length === 0 && !loading.products && (
                    <p className="text-muted mt-1">No products available for version {selectedVersion}</p>
                  )}
                </div>

                {/* Generate Button */}
                <div className="d-grid">
                  <CButton
                    color="primary"
                    size="lg"
                    onClick={handleGenerate}
                    disabled={loading.generation || !selectedProduct}
                    className="py-3"
                  >
                    {loading.generation ? (
                      <>
                        <CSpinner size="sm" className="me-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <CIcon icon={cilCog} className="me-2" />
                        Generate Serial Number
                      </>
                    )}
                  </CButton>
                </div>
              </CForm>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          {/* Generated Serial Number Display */}
          {generatedData && generatedData.serial_number ? (
            <CCard className="mb-4">
              <CCardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Generated Serial Number</h5>
                <CBadge color="success">
                  <CIcon icon={cilCheckCircle} className="me-1" />
                  Generated Successfully
                </CBadge>
              </CCardHeader>
              <CCardBody>
                {/* Product Info */}
                <div className="mb-4">
                  <div className="mb-2">
                    <span className="text-muted">Product:</span>
                    <span className="fw-bold ms-2">{generatedData.product}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted">Version:</span>
                    <span className="fw-bold ms-2">{generatedData.version}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted">Language:</span>
                    <CBadge color="info" className="ms-2">
                      {generatedData.language}
                    </CBadge>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted">Prefix:</span>
                    <CBadge color="secondary" className="ms-2">
                      {generatedData.prefix}
                    </CBadge>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted">License Count:</span>
                    <span className="fw-bold ms-2">{generatedData.license_count}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted">Generated At:</span>
                    <span className="fw-bold ms-2">{formatDate(generatedData.generated_at)}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted">Last Serial:</span>
                    <span className="fw-bold ms-2">{generatedData.last_serial}</span>
                  </div>
                </div>

                {/* Serial Number Display */}
                <div className="mb-4">
                  <CFormLabel className="fw-bold mb-2 d-block">Your Serial Number:</CFormLabel>
                  <div className="p-4 bg-light rounded border text-center">
                    <h2 className="font-monospace mb-0 text-primary">
                      {generatedData.serial_number}
                    </h2>
                  </div>
                </div>

                {/* Copy Button */}
                <div className="d-grid mb-3">
                  <CButton
                    color={copied ? "success" : "primary"}
                    size="lg"
                    onClick={handleCopyToClipboard}
                  >
                    {copied ? (
                      <>
                        <CIcon icon={cilCheckCircle} className="me-2" />
                        Copied to Clipboard!
                      </>
                    ) : (
                      <>
                        <CIcon icon={cilCopy} className="me-2" />
                        Copy Serial Number
                      </>
                    )}
                  </CButton>
                </div>

                <div className="text-center mb-3">
                  <small className="text-muted">
                    Click the button above or select the serial number and copy (Ctrl+C)
                  </small>
                </div>
              </CCardBody>
            </CCard>
          ) : (
            <CCard className="mb-4">
              <CCardHeader>
                <h5 className="mb-0">Generated Serial Number</h5>
              </CCardHeader>
              <CCardBody className="text-center py-5">
                <CIcon icon={cilCog} size="3xl" className="text-muted mb-3" />
                <h6>No Serial Number Generated Yet</h6>
                <p className="text-muted mb-0">
                  Select a product and click "Generate Serial Number" to see the result here.
                </p>
              </CCardBody>
            </CCard>
          )}

          {/* Instructions Card */}
          <CCard>
            <CCardHeader>
              <h5>Instructions</h5>
            </CCardHeader>
            <CCardBody>
              <ol className="mb-0">
                <li><strong>Select</strong> a version (9, 10, or 11)</li>
                <li><strong>Select</strong> a product from the dropdown</li>
                <li><strong>Click</strong> "Generate Serial Number" button</li>
                <li><strong>Copy</strong> the generated serial number</li>
                <li><strong>Use</strong> the serial number as needed</li>
              </ol>
              <div className="mt-3 alert alert-info">
                <strong>Note:</strong> Each click generates a new serial number for the selected product.
                Serial numbers are automatically incremented in the database.
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  );
};

export default SuperAdminSerialNoGeneration;